import { Pill, FileText, X, AlertTriangle } from 'lucide-react';
import type { Prescription } from '../../../../api/prescriptions';

interface PrescriptionDetailViewProps {
  prescription: Prescription;
  onClose: () => void;
}

export function PrescriptionDetailView({
  prescription,
  onClose,
}: PrescriptionDetailViewProps) {
  const formatDate = (d: string | null | undefined) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return d;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-white/8 flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
              <Pill className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Prescription
              </h3>
              <p className="text-sm text-gray-500 dark:text-white/50 font-mono mt-0.5">
                {prescription.prescriptionNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white/70"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Business branding */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-lg p-4 border border-blue-100 dark:border-blue-500/20">
            <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wider font-semibold">
              Issued By
            </p>
            <p className="text-base font-bold text-gray-900 dark:text-white mt-0.5">
              {prescription.businessName}
            </p>
          </div>

          {/* Pet */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 dark:text-white/50">Pet</p>
              <p className="text-gray-900 dark:text-white font-medium">
                {prescription.petName}
              </p>
              {(prescription.petSpecies || prescription.petBreed) && (
                <p className="text-xs text-gray-500 dark:text-white/50">
                  {prescription.petSpecies}
                  {prescription.petBreed ? ` · ${prescription.petBreed}` : ''}
                </p>
              )}
            </div>
            <div>
              <p className="text-gray-500 dark:text-white/50">Owner</p>
              <p className="text-gray-900 dark:text-white">
                {prescription.ownerName || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-white/50">Issued</p>
              <p className="text-gray-900 dark:text-white">
                {formatDate(prescription.issuedAt)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-white/50">Valid Until</p>
              <p className="text-gray-900 dark:text-white">
                {formatDate(prescription.validUntil)}
              </p>
            </div>
          </div>

          {/* Diagnosis */}
          {prescription.diagnosis && (
            <div className="text-sm">
              <p className="text-gray-500 dark:text-white/50 mb-1">Diagnosis</p>
              <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-white/5 p-3 rounded-lg">
                {prescription.diagnosis}
              </p>
            </div>
          )}

          {/* Medications */}
          <div>
            <p className="text-gray-500 dark:text-white/50 text-sm mb-2">
              Medications ({prescription.medications.length})
            </p>
            <div className="space-y-2">
              {prescription.medications.map((med, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 dark:border-white/8 rounded-lg p-3 bg-white dark:bg-white/5"
                >
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-white">
                        {med.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-white/60 mt-0.5">
                        {[med.dosage, med.frequency, med.duration, med.route]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      {med.instructions && (
                        <p className="text-xs text-gray-600 dark:text-white/70 mt-1 italic">
                          {med.instructions}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          {prescription.instructions && (
            <div className="text-sm">
              <p className="text-gray-500 dark:text-white/50 mb-1">
                Dispensing Notes
              </p>
              <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-white/5 p-3 rounded-lg">
                {prescription.instructions}
              </p>
            </div>
          )}

          {/* Remarks */}
          {prescription.remarks && (
            <div className="text-sm">
              <p className="text-gray-500 dark:text-white/50 mb-1">Remarks</p>
              <p className="text-gray-900 dark:text-white">{prescription.remarks}</p>
            </div>
          )}

          {/* Signature Footer */}
          <div className="mt-6 pt-5 border-t border-gray-200 dark:border-white/10">
            {prescription.signatureImageUrl ? (
              <div className="flex flex-col items-center">
                <img
                  src={prescription.signatureImageUrl}
                  alt="Issuer signature"
                  style={{
                    maxHeight: 80,
                    maxWidth: '100%',
                    background: '#fff',
                    borderRadius: 8,
                    padding: 4,
                  }}
                />
                <div className="w-48 border-t border-gray-400 mt-1" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-2">
                  {prescription.signerName || prescription.issuedByName || '—'}
                </p>
                {(prescription.issuedByTitle || prescription.issuedByLicenseNo) && (
                  <p className="text-xs text-gray-500 dark:text-white/60">
                    {prescription.issuedByTitle || ''}
                    {prescription.issuedByTitle && prescription.issuedByLicenseNo
                      ? ' · '
                      : ''}
                    {prescription.issuedByLicenseNo
                      ? `License ${prescription.issuedByLicenseNo}`
                      : ''}
                  </p>
                )}
                {prescription.signedAt && (
                  <p className="text-[11px] text-gray-400 dark:text-white/40 mt-1">
                    Signed on {formatDate(prescription.signedAt)}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center text-xs text-gray-400 dark:text-white/40 italic">
                Unsigned prescription
              </div>
            )}
          </div>

          {/* Revoked banner */}
          {prescription.status === 'revoked' && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 dark:text-red-400 font-medium">
                  This prescription has been revoked
                </p>
                {prescription.revokedReason && (
                  <p className="text-red-600/80 dark:text-red-400/80 text-xs mt-0.5">
                    {prescription.revokedReason}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
